import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { CreateTicketRequest, CustomerOption, SiteOption, ContractOption, SupportTicket, TicketCategory, TicketPriority, TicketRaiseContext, TicketSubCategory } from '../models/ticket.model';
import { TicketService } from '../services/ticket.service';

@Component({ selector: 'ft-ticket-create', templateUrl: './ticket-create.component.html', styleUrls: ['./ticket-create.component.scss'], standalone: false })
export class TicketCreateComponent implements OnChanges {
  @Input() context: TicketRaiseContext | null = null;
  @Input() categories: TicketCategory[] = [];
  @Output() cancelled = new EventEmitter<void>();
  @Output() created = new EventEmitter<SupportTicket>();
  form: FormGroup;
  sites: SiteOption[] = [];
  contracts: ContractOption[] = [];
  subCategories: TicketSubCategory[] = [];
  files: File[] = [];
  submitting = false;
  errorMessage = '';
  readonly priorities: Array<{ label: string; value: TicketPriority }> = [
    { label: 'Low', value: 'LOW' }, { label: 'Medium', value: 'MEDIUM' },
    { label: 'High', value: 'HIGH' }, { label: 'Critical', value: 'CRITICAL' },
  ];

  constructor(private fb: FormBuilder, private tickets: TicketService) {
    this.form = this.fb.group({
      customerId: [null, Validators.required], siteId: [null, Validators.required], contractId: [null, Validators.required],
      categoryCode: [null, Validators.required], subCategoryCode: [null], priority: ['MEDIUM', Validators.required],
      title: ['', [Validators.required, Validators.maxLength(255)]], description: ['', [Validators.required, Validators.maxLength(10000)]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['context'] || changes['categories']) this.initializeDefaults();
  }

  onCustomerChange(): void {
    const selected = this.context?.customers.find(customer => customer.id === this.form.get('customerId')?.value);
    this.sites = selected?.sites ?? [];
    this.form.patchValue({ siteId: null, contractId: null }, { emitEvent: false });
    this.contracts = [];
    if (this.sites.length === 1) { this.form.patchValue({ siteId: this.sites[0].id }); this.onSiteChange(); }
  }

  onSiteChange(): void {
    const selected = this.sites.find(site => site.id === this.form.get('siteId')?.value);
    this.contracts = selected?.contracts ?? [];
    this.form.patchValue({ contractId: null }, { emitEvent: false });
    if (this.contracts.length === 1) this.form.patchValue({ contractId: this.contracts[0].id });
  }

  onCategoryChange(): void {
    const selected = this.categories.find(category => category.code === this.form.get('categoryCode')?.value);
    this.subCategories = selected?.subCategories ?? [];
    this.form.patchValue({ subCategoryCode: null }, { emitEvent: false });
    if (this.subCategories.length === 1) this.form.patchValue({ subCategoryCode: this.subCategories[0].code });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    const accepted = selected.filter(file => file.size <= 10 * 1024 * 1024);
    this.files = [...this.files, ...accepted].slice(0, 5);
    input.value = '';
    if (accepted.length !== selected.length) this.errorMessage = 'Files larger than 10 MB were not added.';
  }

  removeFile(index: number): void { this.files.splice(index, 1); }

  submit(): void {
    this.errorMessage = '';
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const value = this.form.getRawValue();
    const request: CreateTicketRequest = {
      siteId: Number(value.siteId), contractId: Number(value.contractId), categoryCode: String(value.categoryCode),
      subCategoryCode: value.subCategoryCode ? String(value.subCategoryCode) : null, priority: value.priority as TicketPriority,
      title: String(value.title).trim(), description: String(value.description).trim(),
    };
    this.submitting = true;
    this.tickets.create(request).subscribe({
      next: ticket => {
        if (!this.files.length) { this.finish(ticket); return; }
        forkJoin(this.files.map(file => this.tickets.uploadAttachment(ticket.id, file))).subscribe({
          next: responses => this.finish(responses[responses.length - 1] ?? ticket),
          error: error => { this.submitting = false; this.errorMessage = this.error(error, 'Ticket created, but one or more attachments failed to upload.'); this.created.emit(ticket); },
        });
      },
      error: error => { this.submitting = false; this.errorMessage = this.error(error, 'Unable to create the ticket.'); },
    });
  }

  invalid(name: string): boolean { const control = this.form.get(name); return !!control && control.invalid && (control.dirty || control.touched); }
  trackFile(_: number, file: File): string { return `${file.name}-${file.size}`; }

  private initializeDefaults(): void {
    if (this.context?.customers.length === 1 && !this.form.get('customerId')?.value) {
      this.form.patchValue({ customerId: this.context.customers[0].id }); this.onCustomerChange();
    }
  }
  private finish(ticket: SupportTicket): void { this.submitting = false; this.files = []; this.form.reset({ priority: 'MEDIUM' }); this.created.emit(ticket); }
  private error(error: unknown, fallback: string): string {
    const value = error as { error?: { message?: string } }; return value?.error?.message || fallback;
  }
}
